/**
 * Reports-to downline walks over `/user-profile-tasks/subordinates`.
 * Matches backend UserRelationshipHelper::isOnReportsToDownline: full tree, deny self.
 */

/**
 * All profile IDs in a manager's reports-to downline.
 * Does not include the manager themselves.
 *
 * @param {Array<{assigner_id: number|string, assignees?: Array<number|string>}>|null|undefined} subordinates
 * @param {number|string|null} rootProfileId
 * @param {number} [maxDepth=50]
 * @returns {Set<number>}
 */
export function collectReportsToDownlineProfileIds(
    subordinates,
    rootProfileId,
    maxDepth = 50,
) {
    const rootId = Number(rootProfileId);
    if (!rootId || Number.isNaN(rootId)) {
        return new Set();
    }

    const downline = new Set();
    const queue = [rootId];
    const seenManagers = new Set();
    let depth = 0;

    while (queue.length > 0 && depth < maxDepth) {
        const levelCount = queue.length;
        for (let i = 0; i < levelCount; i++) {
            const managerId = queue.shift();
            if (seenManagers.has(managerId)) {
                continue;
            }
            seenManagers.add(managerId);

            const row = (subordinates ?? []).find(
                (sub) => Number(sub.assigner_id) === managerId,
            );
            for (const assigneeId of row?.assignees ?? []) {
                const id = Number(assigneeId);
                if (Number.isNaN(id) || id === managerId) {
                    continue;
                }
                if (!downline.has(id)) {
                    downline.add(id);
                    queue.push(id);
                }
            }
        }
        depth++;
    }

    return downline;
}

/**
 * True when descendantId is strictly on ancestorId's reports-to downline.
 *
 * @param {Array<{assigner_id: number|string, assignees?: Array<number|string>}>|null|undefined} subordinates
 * @param {number|string|null} ancestorId
 * @param {number|string|null} descendantId
 * @param {number} [maxDepth=50]
 * @returns {boolean}
 */
export function isOnReportsToDownline(
    subordinates,
    ancestorId,
    descendantId,
    maxDepth = 50,
) {
    const ancestor = Number(ancestorId);
    const descendant = Number(descendantId);
    if (
        !ancestor ||
        Number.isNaN(ancestor) ||
        !descendant ||
        Number.isNaN(descendant) ||
        ancestor === descendant
    ) {
        return false;
    }

    return collectReportsToDownlineProfileIds(
        subordinates,
        ancestor,
        maxDepth,
    ).has(descendant);
}
